import { useEffect, useRef, useState } from "react";
import { Joyride } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { useTour } from "../../context/TourContext";
// import { dashboardTour } from "../../constants/dashboardTour";
import { tours } from "../../constants";
import { useSelector } from "react-redux";

const TourWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const role = useSelector((state) => state.userReducer.role);

  const { tourState, startTour, stopTour, goToStep } = useTour();
  const { run, stepIndex, tourKey } = tourState;

  const [isTransitioning, setIsTransitioning] = useState(false);
  const pendingStepRef = useRef(null);

  const addHighlight = (selector) => {
    if (!selector || selector === "body") return;

    document.querySelector(selector)?.classList.add("tour-highlight-red");
  };

  const removeHighlights = () => {
    document
      .querySelectorAll(".tour-highlight-red")
      .forEach((el) => el.classList.remove("tour-highlight-red"));
  };

  // When tour starts, navigate to first step's route if needed
  useEffect(() => {
    if (!run) {
      setIsTransitioning(false);
      pendingStepRef.current = null;
      return;
    }
    const firstStep = tours[tourState?.activeTour]?.[0] || [];
    if (firstStep?.route && firstStep.route !== location.pathname) {
      navigate(firstStep.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);
  // After cross-route navigation completes, resume from the pending step
  useEffect(() => {
    if (!isTransitioning || pendingStepRef.current === null) return;

    const pending = pendingStepRef.current;
    const targetRoute = tours[tourState.activeTour]?.[pending]?.route;

    if (!targetRoute || targetRoute === location.pathname) {
      // TOUR CHANGE: wait 500ms after route change
      const timer = setTimeout(() => {
        pendingStepRef.current = null;
        goToStep(pending);
        setIsTransitioning(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, isTransitioning]); // eslint-disable-line react-hooks/exhaustive-deps

  const canAccessRoute = (step) => {
    if (!step.allowedRoles) return true;
    return step.allowedRoles.includes(role);
  };

  const handleCallback = (data) => {
    const { action, index, status, type } = data;

    if (status === "finished" || status === "skipped") {
      stopTour();
      return;
    }

    if (type !== "step:after") return;

    // const nextIndex = action === "prev" ? Math.max(index - 1, 0) : index + 1;

    // if (nextIndex >= tours[tourState.activeTour].length) {
    //   stopTour();
    //   return;
    // }

    // const nextStep = tours[tourState.activeTour][nextIndex];
    // TOUR CHANGE: skip inaccessible routes
    let finalIndex = action === "prev" ? Math.max(index - 1, 0) : index + 1;
    console.log("finalIndex", finalIndex);
    console.log("index", index);
    console.log(
      "Next step allowedRoles:",
      tours[tourState.activeTour][finalIndex]?.allowedRoles
    );

    // while (
    //   finalIndex < tours[tourState.activeTour].length &&
    //   !canAccessRoute(tours[tourState.activeTour][finalIndex].route)
    // ) {
    //   finalIndex++;
    // }
    if (action === "next") {
      while (
        finalIndex < tours[tourState.activeTour].length &&
        !canAccessRoute(tours[tourState.activeTour][finalIndex])
      ) {
        finalIndex++;
      }
    }

    if (finalIndex >= tours[tourState.activeTour].length) {
      stopTour();
      return;
    }

    if (finalIndex >= tours[tourState.activeTour].length) {
      stopTour();
      return;
    }

    const nextStep = tours[tourState.activeTour][finalIndex];

    // TOUR CHANGE: handle cross-route navigation
    if (nextStep?.route && nextStep.route !== location.pathname) {
      setIsTransitioning(true);
      pendingStepRef.current = finalIndex;
      navigate(nextStep.route);
      return;
    }

    // TOUR CHANGE: same route -> move immediately
    goToStep(finalIndex);
  };

  useEffect(() => {
    removeHighlights();
    if (!run || stepIndex != 1) return;

    const currentStep = tours[tourState.activeTour][stepIndex];

    if (currentStep?.target) {
      addHighlight(currentStep.target);
    }
  }, [stepIndex, run]);

  // Don't render Joyride mid-transition — prevents it scanning the wrong page's DOM
  if (!run || isTransitioning) return null;

  return (
    <Joyride
      key={tourKey}
      run={run}
      steps={tours[tourState.activeTour]}
      stepIndex={stepIndex}
      onEvent={handleCallback}
      continuous
      showProgress
      disableScrolling={false}
      spotlightClicks={true}
      // disableScrollParentFix={true}
      // spotlightPadding={8}
      locale={{
        back: "← Back",
        close: "✕",
        last: "Finish Tour",
        next: "Next →",
        skip: "Skip Tour",
      }}
      options={{
        buttons: ["back", "skip", "primary"],
        zIndex: 999,
      }}
      styles={{
        beaconInner: {
          // backgroundColor: "#f56831",
          backgroundColor: "#F54236",
        },
        beaconOuter: {
          backgroundColor: "rgba(188, 157, 157, 0.35)", // red pulse
          border: "2px solid #020000",
        },
        options: {
          zIndex: 99999,
          primaryColor: "#2563eb",
          overlayColor: "rgba(15, 23, 42, 0.75)",
          backgroundColor: "#ffffff",
          textColor: "#111827",
          width: 420,
        },

        tooltip: {
          borderRadius: "16px",
          boxShadow:
            "0 20px 25px -5px rgb(0 0 0 / 0.15), 0 10px 10px -5px rgb(0 0 0 / 0.08)",
        },

        tooltipContainer: {
          textAlign: "left",
          padding: "8px",
        },

        tooltipTitle: {
          fontSize: "18px",
          fontWeight: 700,
          color: "#111827",
          marginBottom: "8px",
        },

        tooltipContent: {
          fontSize: "14px",
          lineHeight: "1.6",
          color: "#4b5563",
        },

        buttonPrimary: {
          backgroundColor: "#2563eb",
          borderRadius: "10px",
          padding: "10px 18px",
          fontWeight: 600,
          fontSize: "14px",
        },

        buttonBack: {
          color: "#6b7280",
          fontWeight: 600,
          marginRight: "8px",
        },

        buttonSkip: {
          color: "#ef4444",
          fontWeight: 600,
          fontSize: "14px",
        },

        buttonClose: {
          color: "#6b7280",
        },

        spotlight: {
          borderRadius: 12,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.7)",
        },
      }}
    />
  );
};

export default TourWrapper;
